type DateUnit = 'days' | 'businessDays' | 'weeks' | 'months';

const DAY_NAME_TO_JS: Record<string, number> = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 0,
};

const DEFAULT_SKIP = new Set([0, 6]);

function resolveSkipDays(skipDays: unknown): Set<number> {
  if (skipDays == null) return DEFAULT_SKIP;
  const list: unknown[] = Array.isArray(skipDays) ? skipDays : [skipDays];
  const result = new Set<number>();
  for (const name of list) {
    if (name == null) continue;
    const nameStr = (typeof name === 'string' ? name : `${name as string | number}`)
      .trim()
      .toUpperCase();
    const jsDay = DAY_NAME_TO_JS[nameStr];
    if (jsDay === undefined) throw new Error(`Unknown day name: ${nameStr}`);
    result.add(jsDay);
  }
  if (result.size >= 7)
    throw new Error('skipDays names every weekday — no working day would remain');
  return result;
}

const DATE_ADD_MAX_ABS = 3650;

// "YYYY-MM-DD" strings are parsed by `new Date()` as UTC midnight; formatting
// via getFullYear/getMonth/getDate uses the local TZ, so on negative-UTC
// offsets (e.g. Pacific) getDate() returns the previous calendar day and
// dateAdd(x, +1, 'days') silently returns x. All arithmetic + formatting
// stays in UTC to avoid that off-by-one.
function toUTCISODate(d: Date): string {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Add `n` units to `date` (YYYY-MM-DD). Mirrors FormulaFunctions.java#dateAdd.
 * Returns null on: falsy/unparseable date, non-finite n, |n| > 3650, unknown unit.
 */
export function dateAdd(
  date: string | null | undefined,
  n: number,
  unit: DateUnit,
  skipDays?: unknown
): string | null {
  if (!date) return null;
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) > DATE_ADD_MAX_ABS) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  switch (unit) {
    case 'days':
      d.setUTCDate(d.getUTCDate() + n);
      break;
    case 'businessDays': {
      const skipSet = resolveSkipDays(skipDays);
      let remaining = Math.abs(n);
      const direction = n >= 0 ? 1 : -1;
      while (remaining > 0) {
        d.setUTCDate(d.getUTCDate() + direction);
        if (!skipSet.has(d.getUTCDay())) remaining--;
      }
      break;
    }
    case 'weeks':
      d.setUTCDate(d.getUTCDate() + n * 7);
      break;
    case 'months':
      d.setUTCMonth(d.getUTCMonth() + n);
      break;
    default:
      return null;
  }

  return toUTCISODate(d);
}

/**
 * ISO-8601 day-of-week (1=Mon … 7=Sun). Mirrors FormulaFunctions.java#dayOfWeek.
 * Uses UTC to stay consistent with dateAdd/dateDiff. Returns 0 when date is null/undefined;
 * throws for unparseable strings (schema-author error, caught by the engine).
 */
export function dayOfWeek(date: string | null | undefined): number {
  if (date == null) return 0;
  const d = new Date(date);
  if (isNaN(d.getTime())) throw new Error(`Unparseable date: ${date}`);
  const jsDay = d.getUTCDay();
  return jsDay === 0 ? 7 : jsDay;
}

/**
 * Difference `b - a` in `unit`. Mirrors FormulaFunctions.java#dateDiff.
 * Returns null on unknown unit; returns 0 when either date is falsy/unparseable.
 */
export function dateDiff(
  a: string | null | undefined,
  b: string | null | undefined,
  unit: DateUnit
): number | null {
  if (!a || !b) return 0;
  const da = new Date(a);
  const db = new Date(b);
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return 0;

  const msPerDay = 86400000;

  switch (unit) {
    case 'days':
      return Math.round((db.getTime() - da.getTime()) / msPerDay);
    case 'businessDays': {
      let count = 0;
      const start = new Date(Math.min(da.getTime(), db.getTime()));
      const end = new Date(Math.max(da.getTime(), db.getTime()));
      const current = new Date(start);
      while (current < end) {
        current.setUTCDate(current.getUTCDate() + 1);
        const day = current.getUTCDay();
        if (day !== 0 && day !== 6) count++;
      }
      return db >= da ? count : -count;
    }
    case 'weeks':
      return Math.round((db.getTime() - da.getTime()) / (msPerDay * 7));
    case 'months':
      return (db.getUTCFullYear() - da.getUTCFullYear()) * 12 + (db.getUTCMonth() - da.getUTCMonth());
    default:
      return null;
  }
}

export function contains(
  coll: unknown[] | Record<string, unknown> | string | null | undefined,
  x: unknown
): boolean {
  if (coll == null) return false;
  if (Array.isArray(coll)) return coll.includes(x);
  if (typeof coll === 'string') return coll.includes(String(x));
  if (typeof coll === 'object') return Object.prototype.hasOwnProperty.call(coll, String(x));
  return false;
}

export function empty(x: unknown): boolean {
  if (x == null) return true;
  if (typeof x === 'string') return x === '';
  if (Array.isArray(x)) return x.length === 0;
  if (typeof x === 'object') return Object.keys(x).length === 0;
  return false;
}

export function size(x: unknown): number {
  if (x == null) return 0;
  if (typeof x === 'string' || Array.isArray(x)) return x.length;
  if (typeof x === 'object') return Object.keys(x).length;
  return 0;
}

export function slug(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function num(x: unknown): number | null {
  if (x == null) return null;
  if (typeof x === 'boolean') return x ? 1 : 0;
  if (typeof x === 'number') return x;
  if (typeof x === 'string') {
    const trimmed = x.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Round `x` to `scale` decimals. Mirrors FormulaFunctions.java#round.
 * String-shift matches Java BigDecimal setScale semantics — avoids IEEE-754
 * divergence on terminal-5 doubles (2.675, 1.005).
 * Returns null when:
 *   - scale is null/undefined, non-integer, negative, or > 15
 *   - x cannot be coerced to a finite number (see num())
 *   - the scaled value is non-finite (|x| >= ~1e21, or exponent overflow at large scale)
 *   - mode is unknown (only 'half_up' | 'ceil' | 'ceiling' | 'floor' are valid)
 *   - the unshifted result is non-finite
 */
export function round(x: unknown, scale: number | null | undefined, mode?: unknown): number | null {
  if (scale == null || !Number.isInteger(scale) || scale < 0 || scale > 15) return null;
  const n = num(x);
  if (n == null || !Number.isFinite(n)) return null;
  const m = (mode == null ? 'half_up' : `${mode as string}`).trim().toLowerCase();
  const shifted = Number(`${n}e${scale}`);
  if (!Number.isFinite(shifted)) return null;
  let rounded: number;
  if (m === 'half_up') {
    rounded = Math.sign(shifted) * Math.round(Math.abs(shifted));
  } else if (m === 'ceil' || m === 'ceiling') {
    rounded = Math.ceil(shifted);
  } else if (m === 'floor') {
    rounded = Math.floor(shifted);
  } else {
    return null;
  }
  const result = Number(`${rounded}e${-scale}`);
  return Number.isFinite(result) ? result : null;
}

export function str(x: unknown): string {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'number' || typeof x === 'boolean') return String(x);
  return JSON.stringify(x);
}
